import React, { useEffect, useMemo, useState } from 'react';
import { Mail, Send } from 'lucide-react';
import { fetchAdminNotifications } from '../../lib/api';

const KIND_LABEL = {
  contact_inquiry: 'Inquiry · in',
  contact_ack: 'Auto-reply · out',
  newsletter_welcome: 'Newsletter welcome',
  order_confirmation: 'Order confirmation',
  seva_token_award: 'SEVA token award',
  partner_application: 'Partner application',
  refill_seva_award: 'Refill · SEVA awarded',
};

const ALL = '__all__';

const NotificationsLog = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(ALL);

  useEffect(() => {
    fetchAdminNotifications(100).then(setData).catch(() => setData({ count: 0, notifications: [] }));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === ALL) return data.notifications;
    return data.notifications.filter((n) => n.kind === filter);
  }, [data, filter]);

  const kinds = useMemo(() => Object.keys(data?.counts_by_kind || {}), [data]);

  if (!data)
    return (
      <div
        data-testid="notifications-loading"
        className="h-40 bg-[#C1D7E2]/40 animate-pulse"
      />
    );

  return (
    <div data-testid="notifications-view" className="space-y-8">
      <header className="border-b border-[#C1D7E2] pb-6">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-2">
          Correspondence Audit
        </p>
        <h3 className="font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light">
          Outbound &amp; inbound mail
        </h3>
        <p className="mt-2 font-serif italic text-[#90B0BF]">
          Every guest, partner, and SEVA-award notification routed through{' '}
          <span className="not-italic font-mono text-[#7C9CB1]">
            {data.delivery_status?.from_address}
          </span>
          .
        </p>
        {data.delivery_status?.MOCKED && (
          <p
            data-testid="notifications-mock-banner"
            className="mt-4 inline-flex items-center gap-2 px-3 py-2 border border-[#90B0BF] bg-[#F4F8F9] font-mono text-[10px] tracking-[0.18em] uppercase text-[#7C9CB1]"
          >
            <Send size={12} strokeWidth={1.4} />
            MOCKED · audit-only · provision Resend / SendGrid to enable delivery
          </p>
        )}
      </header>

      {/* Filter chips */}
      <div data-testid="notifications-filter" className="flex flex-wrap gap-2">
        <FilterChip
          active={filter === ALL}
          onClick={() => setFilter(ALL)}
          testId="notifications-filter-all"
          label={`All · ${data.count}`}
        />
        {kinds.map((k) => (
          <FilterChip
            key={k}
            active={filter === k}
            onClick={() => setFilter(k)}
            testId={`notifications-filter-${k}`}
            label={`${KIND_LABEL[k] || k} · ${data.counts_by_kind[k]}`}
          />
        ))}
      </div>

      {/* Mail list */}
      {filtered.length === 0 ? (
        <p
          data-testid="notifications-empty"
          className="font-serif italic text-[#90B0BF] py-10 text-center"
        >
          No correspondence under this filter yet.
        </p>
      ) : (
        <ul data-testid="notifications-list" className="space-y-4">
          {filtered.map((n, i) => (
            <li
              key={n.id}
              data-testid={`notification-row-${i}`}
              className="border border-[#C1D7E2] bg-[#F4F8F9] p-5 hover:border-[#7C9CB1] transition-colors"
            >
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 mb-3">
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#90B0BF]">
                  {KIND_LABEL[n.kind] || n.kind}
                </span>
                <span className="font-mono text-[10px] tracking-[0.18em] text-[#90B0BF]">
                  {n.created_at}
                </span>
                <span
                  className={`font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 border ${
                    n.delivered
                      ? 'border-[#7C9CB1] text-[#7C9CB1]'
                      : 'border-[#90B0BF] text-[#90B0BF]'
                  }`}
                >
                  {n.delivered ? 'Delivered' : 'Queued'}
                </span>
              </div>
              <p className="font-serif text-lg text-[#7C9CB1] leading-snug mb-2">
                {n.subject}
              </p>
              <div className="flex flex-wrap gap-x-4 font-mono text-[11px] text-[#7C9CB1] mb-3">
                <span className="flex items-center gap-1">
                  <Mail size={11} strokeWidth={1.4} />
                  {n.sender}
                </span>
                <span className="text-[#90B0BF]">→</span>
                <span>{n.recipient}</span>
              </div>
              <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-[#7C9CB1]/85 bg-white/40 p-3 border border-[#C1D7E2]/60">
                {n.body}
              </pre>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FilterChip = ({ active, onClick, label, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-medium border transition-colors ${
      active
        ? 'border-[#7C9CB1] bg-[#7C9CB1] text-[#F4F8F9]'
        : 'border-[#C1D7E2] text-[#7C9CB1] hover:bg-[#C1D7E2]/40'
    }`}
  >
    {label}
  </button>
);

export default NotificationsLog;
